// FILE: src/hooks/useIdleTimer.js
// Client half of MTO 1.4 (the server enforces the real 15-minute limit).
// Counts as activity: pointer, keyboard, scroll, touch, the tab becoming
// visible, and anything reported through utils/activity (the course iframe;
// SCORM commits from WP-4) — B-16. onIdle fires once.
import { useEffect, useRef } from "react";
import { onActivity } from "../utils/activity";

const EVENTS = ["mousemove", "pointerdown", "keydown", "scroll", "touchstart", "wheel"];

export default function useIdleTimer({ warningTimeout, sessionTimeout, onWarn, onIdle, onActive }) {
  const lastActive = useRef(Date.now());
  const warned = useRef(false);
  const idled = useRef(false);
  const cb = useRef({ onWarn, onIdle, onActive });
  cb.current = { onWarn, onIdle, onActive };

  useEffect(() => {
    function handleActivity() {
      if (idled.current) return;
      lastActive.current = Date.now();
      warned.current = false;
      cb.current.onActive?.();
    }
    function handleVisibility() {
      if (document.visibilityState === "visible") handleActivity();
    }
    EVENTS.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibility);
    const off = onActivity(handleActivity);

    const timer = setInterval(() => {
      if (idled.current) return;
      const elapsed = Date.now() - lastActive.current;
      if (elapsed >= sessionTimeout) {
        idled.current = true;
        cb.current.onIdle?.();
      } else if (elapsed >= warningTimeout && !warned.current) {
        warned.current = true;
        cb.current.onWarn?.();
      }
    }, 1000);

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      off();
      clearInterval(timer);
    };
  }, [warningTimeout, sessionTimeout]);

  return null;
}
